import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MODAL_SIZES, ORDER_METHODS } from '../../services/exports/Constants';
import CustomModal from '../../components-library/CustomModal';
import useScreenType from '../../hooks/utility/useScreenType';
import useAppState from '../../hooks/global/useAppState';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import CloseCircleIcon from '../../assets/icons/CloseCircleIcon';
import OrderMethodsTabBar from '../../components-library/navigation/OrderMethodsTabBar';
import CompaniesList from './CompaniesList';
import CustomButton from '../../components-library/buttons/CustomButton';
import LocationDataProvider, { useLocationData } from './provider/LocationDataProvider';
import { selectCompany } from '../../store/slices/InitialDataSlice';
import { useTranslation } from 'react-i18next';
import { useGetFranchiseDataQuery } from '../../store/api-slices';
import useDeliveryCompatibleStuff from './useDeliveryCompatibleStuff';
import { useFormContext } from 'react-hook-form';
import ScheduledOrderModal from './ScheduledOrderModal';

export enum TIME_OPTIONS {
  asap = 'asap',
  scheduled = 'scheduled',
}

export enum ValidationErrors {
  address_not_found = 'address_not_found',
  address_not_serviceable = 'address_not_serviceable',
  no_drivers_available = 'no_drivers_available',
}

export default function OrderMethodModal() {
  const { openModal, isScheduledModalOpen, company } = useAppState().orderMethodModalProps;
  const { isDesktop } = useScreenType();
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const hasLiveOrders = useSelector(selectCompany).has_live_orders;
  const isModalClosable = isOrderMethodInfoComplete || !hasLiveOrders;
  const { toggleOrderMethodModal } = useAppMethods();

  function toggleModal(): void {
    toggleOrderMethodModal({ openModal: false });
  }

  return (
    <CustomModal
      open={openModal}
      size={MODAL_SIZES.SM}
      fullScreen={!isDesktop}
      onClose={isModalClosable ? toggleModal : null}
      containerClassName="h-[90dvh]"
    >
      <LocationDataProvider>
        {isScheduledModalOpen ? <ScheduledOrderModal company={company} /> : <Content />}
      </LocationDataProvider>
    </CustomModal>
  );
}

const Content = () => {
  const { orderMethod: propsOrderMethod } = useAppState().orderMethodModalProps;
  const { isOrderMethodInfoComplete } = useOrderFlow();
  const hasLiveOrders = useSelector(selectCompany).has_live_orders;
  const isModalClosable = isOrderMethodInfoComplete || !hasLiveOrders;
  const deliveryAddressForm = useFormContext();
  const { isValid } = deliveryAddressForm.formState;
  const { orderMethod, setOrderMethod, save, loading, company, setCompany, scheduledFor } = useLocationData();
  const { toggleOrderMethodModal } = useAppMethods();
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:OrderMethodModal' });

  function toggleModal(): void {
    toggleOrderMethodModal({ openModal: false });
  }

  const { data, isLoading } = useGetFranchiseDataQuery(false, {
    selectFromResult: (res) => {
      const companies = res.data?.data.companies?.filter((company) => company.has_live_orders);

      return {
        ...res,
        data: {
          ...res.data,
          companies,
        },
      };
    },
  });

  const { handle, queryState, isLoading: isDeliveryStuffLoading } = useDeliveryCompatibleStuff();

  useEffect(() => {
    const companies = data?.data?.companies;

    if (!companies?.length || companies?.length > 1 || !!company) {
      return;
    }

    setCompany(companies[0]);
  }, [data?.data?.companies, company]);

  useEffect(() => {
    if (!propsOrderMethod) {
      return;
    }

    setOrderMethod(propsOrderMethod);
  }, []);

  const hasOnSiteTab = data?.data?.companies?.some((company) => company.has_room_service);
  const forcedOrderMethods = hasOnSiteTab ? [ORDER_METHODS.ROOM_SERVICE] : [];
  const saveButtonTitle = useMemo(() => {
    if (orderMethod !== ORDER_METHODS.DELIVERY) {
      return t('buttons.view_menu');
    }

    if (queryState.isUninitialized) {
      return t('buttons.next');
    }

    return t('buttons.view_menu');
  }, [orderMethod, queryState.isUninitialized]);

  const onCustomBtnClick = () => {
    if (orderMethod !== ORDER_METHODS.DELIVERY || !queryState.isUninitialized) {
      return save();
    }

    return handle();
  };
  const isCustomBtnDisabled =
    isLoading ||
    loading ||
    isDeliveryStuffLoading ||
    (orderMethod !== ORDER_METHODS.DELIVERY && !company) ||
    (orderMethod !== ORDER_METHODS.DELIVERY && scheduledFor === undefined) ||
    (orderMethod === ORDER_METHODS.DELIVERY && !queryState.isUninitialized && scheduledFor === undefined) ||
    (orderMethod === ORDER_METHODS.DELIVERY && !queryState.isUninitialized && !company) ||
    (orderMethod === ORDER_METHODS.DELIVERY &&
      !queryState.isUninitialized &&
      !queryState.data?.includes(company?.id)) ||
    (orderMethod === ORDER_METHODS.DELIVERY && queryState.isUninitialized && !isValid) ||
    (orderMethod === ORDER_METHODS.DELIVERY && !queryState.isUninitialized && !queryState.data?.length) ||
    (orderMethod === ORDER_METHODS.ROOM_SERVICE && !company.has_room_service);
  const isCustomBtnLoading = loading || isLoading || isDeliveryStuffLoading;

  return (
    <div className="w-full pb-md lg:pb-lg px-md lg:px-lg bg-white space-y-md flex flex-col h-full">
      <div className="bg-white pt-md lg:pt-lg space-y-md">
        <div className="flex justify-between items-center">
          <h3 className="font-medium">{t('title')}</h3>
          {isModalClosable && (
            <button onClick={toggleModal}>
              <CloseCircleIcon />
            </button>
          )}
        </div>
        <OrderMethodsTabBar
          orderMethod={orderMethod}
          onClick={setOrderMethod}
          className="bg-gray"
          company={company}
          forcedOrderMethods={forcedOrderMethods}
        />
      </div>
      <CompaniesList data={data?.data} className="h-full overflow-y-auto" />
      <CustomButton
        title={saveButtonTitle}
        onClick={onCustomBtnClick}
        loading={isCustomBtnLoading}
        disabled={isCustomBtnDisabled}
      />
    </div>
  );
};
